import { FC, useEffect, useState } from "react";

import Stack from "@mui/material/Stack";
import { isTaxRegistrationNumberPrefilled, isTaxRegistrationNumberRequired } from "@simplyk/common";
import { enqueueSnackbar } from "notistack";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { DeleteTaxDialog } from "@/features/ManageCollectableTaxes/DeleteTaxDialog/DeleteTaxDialog";
import {
  formatRatePpm,
  getTaxRowErrorKeys,
  isTaxNameDuplicated,
  normalizeTaxName,
  parseRatePercent,
  TaxRowErrors,
} from "@/features/ManageCollectableTaxes/helpers/useManageCollectableTaxes";
import { TaxRowFields } from "@/features/ManageCollectableTaxes/TaxRowFields/TaxRowFields";
import { TicketingTax, useTicketingTaxes } from "@/features/LiveFormEditor/Steps/StepTickets/hooks/useTicketingTaxes";
import { Amount, Delete, Edit } from "@/icons/outlined";

const RATE_PPM_PER_PERCENT = 10_000;

interface TaxFormModalProps {
  open: boolean;
  tax: TicketingTax | null;
  onClose: () => void;
  onCreated?: (tax: TicketingTax) => void;
  onDeleted?: (deletedId: string) => void;
}

export const TaxFormModal: FC<TaxFormModalProps> = ({ open, tax, onClose, onCreated, onDeleted }) => {
  const { t } = useTranslate();
  const { organization } = useCurrentUserContext();
  const { taxes, createTax, updateTax, deleteTax, isSaving, isDeleting } = useTicketingTaxes();

  const isEdit = tax !== null;

  const [values, setValues] = useState({ name: "", rate: "", taxRegistrationNumber: "" });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    const prefilledRegistrationNumber = isTaxRegistrationNumberPrefilled(organization?.country)
      ? (taxes.find((existingTax) => existingTax.taxRegistrationNumber)?.taxRegistrationNumber ?? "")
      : "";
    setValues(
      tax
        ? {
            name: tax.name,
            rate: formatRatePpm(tax.ratePpm),
            taxRegistrationNumber: tax.taxRegistrationNumber ?? "",
          }
        : { name: "", rate: "", taxRegistrationNumber: prefilledRegistrationNumber }
    );
    setHasSubmitted(false);
    setIsConfirmDeleteOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tax, organization?.country]);

  const isRegistrationNumberRequired = isTaxRegistrationNumberRequired(organization?.country);
  const otherTaxNames = taxes
    .filter((existingTax) => existingTax.id !== tax?.id)
    .map((existingTax) => normalizeTaxName(existingTax.name));

  const errorKeys = getTaxRowErrorKeys(values, {
    isNameDuplicated: isTaxNameDuplicated(values.name, [normalizeTaxName(values.name)], otherTaxNames),
    isRegistrationNumberRequired,
  });
  const errorMessages: Record<string, string> = {
    nameRequired: t("dashboard", "taxes.manage.nameRequiredError"),
    nameDuplicated: t("dashboard", "taxes.manage.nameDuplicatedError"),
    rateRequired: t("dashboard", "taxes.manage.rateRequiredError"),
    rateInvalid: t("dashboard", "taxes.manage.rateInvalidError"),
    taxIdRequired: t("dashboard", "taxes.manage.taxIdRequiredError"),
  };
  const errors: TaxRowErrors = {
    name: errorKeys.name ? errorMessages[errorKeys.name] : undefined,
    rate: errorKeys.rate ? errorMessages[errorKeys.rate] : undefined,
    taxRegistrationNumber: errorKeys.taxRegistrationNumber ? errorMessages[errorKeys.taxRegistrationNumber] : undefined,
  };

  const handleSubmit = async () => {
    setHasSubmitted(true);
    if (errorKeys.name || errorKeys.rate || errorKeys.taxRegistrationNumber) {
      return;
    }
    const input = {
      name: values.name.trim(),
      ratePpm: Math.round((parseRatePercent(values.rate) ?? 0) * RATE_PPM_PER_PERCENT),
      taxRegistrationNumber: values.taxRegistrationNumber.trim() || null,
    };
    if (isEdit) {
      const { errorMessage } = await updateTax(tax.id, input);
      if (errorMessage !== undefined) {
        enqueueSnackbar(errorMessage || t("dashboard", "taxes.manage.saveError"), { vibe: "danger" });
        return;
      }
    } else {
      const { tax: createdTax, errorMessage } = await createTax(input);
      if (!createdTax) {
        enqueueSnackbar(errorMessage || t("dashboard", "taxes.manage.saveError"), { vibe: "danger" });
        return;
      }
      onCreated?.(createdTax);
    }
    onClose();
  };

  const handleConfirmDelete = async () => {
    if (!tax) {
      return;
    }
    const { deletedId, errorMessage } = await deleteTax(tax.id);
    if (!deletedId) {
      enqueueSnackbar(errorMessage || t("dashboard", "taxes.manage.deleteError"), { vibe: "danger" });
      return;
    }
    onDeleted?.(deletedId);
    setIsConfirmDeleteOpen(false);
    onClose();
  };

  return (
    <>
      <DashboardDialog open={open && !isConfirmDeleteOpen} onClose={onClose} maxWidth="sm">
        <DashboardDialogBody
          slots={{
            header: (
              <DashboardDialogHeader
                icon={isEdit ? <Edit /> : <Amount />}
                title={t("dashboard", isEdit ? "ticketing.taxes.modal.editTitle" : "ticketing.taxes.modal.addTitle")}
              />
            ),
            content: (
              <DashboardDialogContent>
                <Stack spacing={1.5}>
                  {isEdit && (
                    <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                      {t("dashboard", "ticketing.taxes.modal.editDescription")}
                    </DashboardTypography>
                  )}
                  <TaxRowFields
                    key={`${tax?.id ?? "new"}-${String(open)}`}
                    row={{ key: tax?.id ?? "new", ...values }}
                    errors={errors}
                    onChange={(patch) => setValues((prev) => ({ ...prev, ...patch }))}
                    showLabels
                    forceShowErrors={hasSubmitted}
                    dataTestPrefix="rate-tax-modal"
                    endAction={
                      isEdit ? (
                        <DashboardIconButton
                          variant="outlined"
                          vibe="danger"
                          aria-label={t("dashboard", "taxes.manage.deleteConfirm")}
                          onClick={() => setIsConfirmDeleteOpen(true)}
                          data-test="rate-tax-modal-delete"
                        >
                          <Delete />
                        </DashboardIconButton>
                      ) : undefined
                    }
                  />
                </Stack>
              </DashboardDialogContent>
            ),
            footer: (
              <DashboardDialogFooter>
                <DashboardButton
                  onClick={() => void handleSubmit()}
                  isLoading={isSaving}
                  data-test="rate-tax-modal-submit"
                >
                  {t("dashboard", isEdit ? "ticketing.taxes.modal.applyChanges" : "ticketing.taxes.modal.applyTax")}
                </DashboardButton>
                <DashboardButton vibe="neutral" variant="outlined" onClick={onClose}>
                  {t("dashboard", "common.cancel")}
                </DashboardButton>
              </DashboardDialogFooter>
            ),
          }}
        />
      </DashboardDialog>
      {isConfirmDeleteOpen && tax && (
        <DeleteTaxDialog
          taxName={tax.name}
          isDeleting={isDeleting}
          onConfirm={() => void handleConfirmDelete()}
          onCancel={() => setIsConfirmDeleteOpen(false)}
        />
      )}
    </>
  );
};
